import React from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Grid3X3, List, Search, ChevronUp, ChevronDown } from 'lucide-react';
import { useScreenSize } from '@/hooks/useScreenSize';

/**
 * Layout standardisé pour toutes les pages de gestion
 * Structure : En-tête → Stats → Filtres → Sections → Recherche → Données → Pagination
 */
const StandardPageLayout = ({
  // En-tête
  title,
  statsData = [],
  actionButtons = [],
  
  // Filtres et contrôles
  filters = [],
  sortOptions = [],
  currentSort,
  onSortChange,
  
  // Modes d'affichage
  viewMode,
  onViewModeChange,
  
  // Sections toggles (catégories, races, graphiques, etc.)
  sections = [],
  
  // Recherche
  searchPlaceholder = "Rechercher...",
  searchValue,
  onSearchChange,
  
  // Données
  tableView,
  cardsView,
  
  // Pagination
  currentPage,
  totalPages,
  onPageChange,
  itemsCount,
  
  // Loading
  loading = false,
  
  children
}) => {
  const { isMobile } = useScreenSize();

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-600 dark:border-slate-400"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* 1. EN-TÊTE avec titre + boutons action */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">{title}</h1>
          {statsData.length > 0 && (
            <div className="flex items-center gap-4 mt-2">
              {statsData.map((stat, index) => (
                <React.Fragment key={stat.label || `stat-${index}`}>
                  <p className={`text-gray-600 ${stat.color || ''}`}>
                    <span className="font-semibold">{stat.value}</span> {stat.label}
                  </p>
                  {index < statsData.length - 1 && <span className="text-gray-400">•</span>}
                </React.Fragment>
              ))}
            </div>
          )}
        </div>
        <div className="flex gap-2 flex-wrap">
          {actionButtons.map((button, index) => (
            <Button
              key={button.testId || button.label || `btn-${index}`}
              onClick={button.onClick}
              variant={button.variant || 'default'}
              className={button.className || ''}
              data-testid={button.testId}
            >
              {button.icon && <button.icon className="w-4 h-4 mr-2" />}
              <span className="hidden sm:inline">{button.label}</span>
              <span className="sm:hidden">{button.shortLabel || button.label}</span>
            </Button>
          ))}
        </div>
      </div>

      {/* 2. TABLEAU DE BORD (stats détaillées) */}
      {children}

      {/* 3. FILTRES ET OPTIONS - seulement si nécessaire */}
      {(filters.length > 0 || sortOptions.length > 0 || onViewModeChange || sections.length > 0) && (
      <Card className="border-0 shadow-md">
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Filtres dynamiques */}
            {filters.map((filter, index) => (
              <div key={filter.label || `filter-${index}`} className="space-y-2">
                <Label>{filter.label}</Label>
                <Select value={filter.value} onValueChange={filter.onChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {filter.options.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ))}

            {/* Tri */}
            {sortOptions.length > 0 && (
              <div className="space-y-2">
                <Label>Trier par</Label>
                <Select value={currentSort} onValueChange={onSortChange}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {sortOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Mode d'affichage - seulement si onViewModeChange est fourni */}
            {onViewModeChange && (
              <div className="space-y-2">
                <Label>Affichage</Label>
                <div className="flex gap-1">
                  {!isMobile && (
                    <Button
                      type="button"
                      variant={viewMode === 'table' ? 'default' : 'outline'}
                      size="sm"
                      onClick={() => onViewModeChange('table')}
                      className="flex-1"
                    >
                      <List className="w-4 h-4 mr-1" />
                      <span className="hidden sm:inline">Tableau</span>
                    </Button>
                  )}
                  <Button
                    type="button"
                    variant={viewMode === 'cards' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => onViewModeChange('cards')}
                    className={isMobile ? "w-full" : "flex-1"}
                  >
                    <Grid3X3 className="w-4 h-4 mr-1" />
                    <span className="hidden sm:inline">Cartes</span>
                    <span className="sm:hidden">Cartes (mobile)</span>
                  </Button>
                </div>
              </div>
            )}

            {/* Résultats - seulement si itemsCount est fourni */}
            {itemsCount !== undefined && (
              <div className="space-y-2">
                <Label>Résultats</Label>
                <div className="h-10 flex items-center px-3 bg-gray-50 rounded-lg border">
                  <span className="text-sm font-medium text-gray-700">
                    {itemsCount} élément(s) affiché(s)
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Sections toggles */}
          {sections.length > 0 && (
            <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {sections.map((section, index) => (
                <Button
                  key={section.title || `section-${index}`}
                  variant="outline"
                  size="sm"
                  onClick={section.onToggle}
                  className="justify-between"
                >
                  <div className="flex items-center gap-2">
                    <section.icon className="w-4 h-4" />
                    {section.title}
                  </div>
                  {section.visible ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                </Button>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}

      {/* 4. SECTIONS AVEC CONTENU */}
      {sections.map((section, index) => 
        section.visible && section.content && (
          <Card key={section.title || `section-card-${index}`} className="border-0 shadow-md">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-lg flex items-center gap-2">
                  <section.icon className="w-5 h-5 text-slate-700 dark:text-slate-300" />
                  {section.title}
                </CardTitle>
                {section.actionButton}
              </div>
            </CardHeader>
            <CardContent>
              {section.content}
            </CardContent>
          </Card>
        )
      )}

      {/* 5. RECHERCHE */}
      {searchValue !== undefined && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
          <Input
            placeholder={searchPlaceholder}
            value={searchValue}
            onChange={(e) => onSearchChange(e.target.value)}
            className="pl-10 border-gray-200 focus:border-slate-500"
            data-testid="search-input"
          />
        </div>
      )}

      {/* 6. AFFICHAGE DONNÉES */}
      {viewMode === 'table' && tableView}
      {viewMode === 'cards' && cardsView}

      {/* 7. PAGINATION (intégrée dans les vues) */}
    </div>
  );
};

export default StandardPageLayout;